import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { Frequency } from 'components/Frequency'
import { RepeatType } from 'components/Frequency/constants'
import { validateFrequencyData } from 'components/Frequency/logic'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { submitReminderTemplate } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { reminderTemplateInputs } from '../constant'
import type { IReminderTemplate } from './types'

const defaultFrequency = () => ({ repeatType: RepeatType.NoRepeat })

export function ReminderTemplateDetails(props: {
  defaults: IReminderTemplate | null
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
}) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [frequency, setFrequency] = useState<Record<string, any>>(defaultFrequency())
  const [errorCheck, setErrorCheck] = useState(false)
  const { defaults = null, lastUpdatedAt } = props

  useEffect(() => {
    if (!defaults) {
      setInputs(reminderTemplateInputs())
      setFrequency(defaultFrequency())
      return
    }
    const newStats = cloneDeep(reminderTemplateInputs()) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    setInputs(newStats)
    setFrequency(defaults.frequency || defaultFrequency())
  }, [defaults, lastUpdatedAt])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    hasError = hasError || validateFrequencyData(frequency)
    setErrorCheck(true)

    if (hasError) {
      setInputs(newStats)
      return
    }

    data.frequency = frequency

    setLoading(true)
    const id = defaults ? defaults.id : 0
    submitReminderTemplate(id, data)
      .then(() => {
        if (id == 0) toast('New reminder template is added.', { type: 'success' })
        else toast('Reminder template is updated.', { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  return (
    <div className="reminder-template-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Reminder Templates - {!defaults ? 'Add' : 'Update'}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Reminder Templates</p>
        </div>
      </Button>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-4">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          return (
            <div className={`input col-span-${input.span || 1}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>

      <div className="mb-4">
        <Frequency data={frequency} onChange={(value: any) => setFrequency(value)} errorCheck={errorCheck} />
      </div>

      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'} Reminder Template</>
        </Button>
      </div>
    </div>
  )
}
